import React, { useCallback, useEffect, useMemo, useState } from "react";
import { InfiniteScrollList, InputSearch, Modal } from "../../../components";
import { useDocs } from "../../../hooks";
import { attachAnnexesAPI, detachAnnexAPI } from "../../../common/services";
import { useToaster } from "../../../common/contexts/toast";
import { errorMessageOf } from "../../../utils";
import { getParentId, isAnnexDoc } from "../utils";
import { buildDefaultDocsFilterValue, getDocFilesOptions } from "../../../constants/docs";
import { WizardActionChoice, WizardDocsPanel, WizardRightPanel } from "./wizard-shared";

// The chosen action titles the panel, names the confirm button and becomes the last breadcrumb.
const ACTION_OPTIONS = [
    { id: "select-base", lead: "Select a base-file", rest: "(Parent) for the files", title: "Select a base-file (Parent)", applyLabel: "APPLY" },
    { id: "detach",      lead: "Detach annex",       rest: "files",                  title: "Detach annex files" },
];

const CONTINUE_ICON = "/images/icon/dotted-arrow.svg";
const BASE_FILE_DESCRIPTION = 'Select the Base file that you want to define as "Parent".';

const BASE_FILE_FILTER_VALUE = buildDefaultDocsFilterValue(getDocFilesOptions().map((option) => option.id));

const SEARCH_DEBOUNCE_MS = 400;

const getSearchText = (filterValue) => filterValue?.displayName || "";

const getActionOption = (actionId) => ACTION_OPTIONS.find((option) => option.id === actionId);

export const getAnnexActionTitle = (actionId) => getActionOption(actionId)?.title;

// Two-step wizard: step one (`action` = null) picks the action, step two the
// base-file. The page owns `action` because it also names a breadcrumb.
const ManageAnnexesWizard = ({ docs = [], allDocs = [], selectedIds, action, onChangeAction, onApply }) => {
    const [draftAction, setDraftAction] = useState(action || null);
    const [parentId, setParentId] = useState(null);
    const [confirmDetach, setConfirmDetach] = useState(false);
    const [applying, setApplying] = useState(false);
    const { showToaster } = useToaster();

    // Going back to step one keeps the previous choice ticked.
    useEffect(() => {
        if (action) setDraftAction(action);
    }, [action]);

    const {
        docsList: baseFiles,
        isLoading: baseLoading,
        isScrollLoader: baseLoadingMore,
        handleScroll: handleBaseScroll,
        filterValue: baseFilterValue,
        onChangeFilter: onChangeBaseFilter,
    } = useDocs({ defaultFilterValue: BASE_FILE_FILTER_VALUE });

    const onSearchBaseFiles = useCallback((text) => {
        const displayName = (text || "").trim();
        if (displayName === getSearchText(baseFilterValue)) return;
        onChangeBaseFilter({ ...baseFilterValue, displayName });
    }, [baseFilterValue, onChangeBaseFilter]);

    const shownDocs = useMemo(() => {
        const related = new Map();
        docs.forEach((doc) => {
            const parentDocId = getParentId(doc);
            if (parentDocId && !selectedIds?.has(parentDocId)) {
                const parent = allDocs.find((d) => d.id === parentDocId);
                if (parent) related.set(parent.id, parent);
            }
            allDocs.forEach((d) => {
                if (!selectedIds?.has(d.id) && getParentId(d) === doc.id) related.set(d.id, d);
            });
        });
        return [...docs, ...related.values()];
    }, [docs, allDocs, selectedIds]);

    // Only annexes can be detached; any file can become one. Keyed off `draftAction`
    // so the list reacts on tick — "Detach" never reaches step two.
    const getRowDisabled = useCallback(
        (doc) => {
            if (!selectedIds?.has(doc.id)) return true;
            if (draftAction === "detach") return !isAnnexDoc(doc);
            return false;
        },
        [selectedIds, draftAction]
    );

    // Attach acts on all selected files; detach acts only on the selected annexes.
    const eligibleDocIds = useMemo(() => docs.map((doc) => doc.id), [docs]);
    const detachTargets = useMemo(
        () => docs.filter((doc) => isAnnexDoc(doc)).map((doc) => ({ id: doc.id, parentId: getParentId(doc) })),
        [docs]
    );
    const activeOption = getActionOption(action);
    const inValuesStep = !!activeOption;

    // Step one: only offer to continue with an action this selection can run.
    const canContinue = useMemo(() => {
        if (!draftAction) return false;
        if (draftAction === "select-base") return eligibleDocIds.length > 0;
        return detachTargets.length > 0; // detach
    }, [draftAction, eligibleDocIds, detachTargets]);

    const canApply = useMemo(
        () => !!parentId && eligibleDocIds.length > 0,
        [parentId, eligibleDocIds]
    );

    // "Detach" needs no base-file, so it skips step two and confirms in a dialog.
    const handleContinue = useCallback(() => {
        if (!canContinue) return;
        if (draftAction === "detach") {
            setConfirmDetach(true);
            return;
        }
        setParentId(null);
        onSearchBaseFiles("");
        onChangeAction && onChangeAction(draftAction);
    }, [canContinue, draftAction, onChangeAction, onSearchBaseFiles]);

    const applyAction = useCallback(async (actionId) => {
        if (!actionId || applying) return;
        setApplying(true);
        try {
            if (actionId === "select-base") {
                await attachAnnexesAPI({ parentId, annexDocIds: eligibleDocIds });
            } else {
                await Promise.all(
                    detachTargets.map((t) => detachAnnexAPI({ parentId: t.parentId, annexId: t.id }))
                );
            }
            showToaster && showToaster(
                "manage-annexes-success",
                actionId === "select-base" ? "Annexes attached." : "Annexes detached.",
                true
            );
            onApply && onApply();
        } catch (error) {
            showToaster && showToaster(
                "manage-annexes-error",
                errorMessageOf(error, "Couldn't update annexes."),
                false
            );
        } finally {
            setApplying(false);
            setConfirmDetach(false);
        }
    }, [applying, parentId, eligibleDocIds, detachTargets, onApply, showToaster]);

    return (
        <div className="docs-wizard">
            <WizardDocsPanel docs={shownDocs} count={docs.length} getRowDisabled={getRowDisabled} />

            <WizardRightPanel
                title={inValuesStep ? activeOption.title : "Manage annexes"}
                iconSrc="/images/icon/list-item-child.svg"
                canApply={inValuesStep ? canApply : canContinue}
                applying={applying}
                applyLabel={inValuesStep ? (activeOption.applyLabel || "APPLY") : "CONTINUE"}
                applyIconSrc={inValuesStep ? undefined : CONTINUE_ICON}
                onApply={inValuesStep ? () => applyAction(action) : handleContinue}
            >
                {inValuesStep ? (
                    <div className="annex-wizard-base">
                        <p className="annex-wizard-base-description">{BASE_FILE_DESCRIPTION}</p>
                        <InputSearch
                            searchText={getSearchText(baseFilterValue)}
                            onChangeSearchText={onSearchBaseFiles}
                            debounceTime={SEARCH_DEBOUNCE_MS}
                            placeholder="Search base-file by name"
                        />
                        <InfiniteScrollList
                            className="annex-wizard-base-list"
                            isLoading={baseLoading}
                            isLoadingMore={baseLoadingMore}
                            onScroll={handleBaseScroll}
                            emptyText="No base-files found"
                            emptySize="small"
                        >
                            {baseFiles.map((doc) => (
                                <BaseFileRow
                                    key={doc.id}
                                    doc={doc}
                                    selected={doc.id === parentId}
                                    disabled={selectedIds?.has(doc.id)}
                                    onSelect={setParentId}
                                />
                            ))}
                        </InfiniteScrollList>
                    </div>
                ) : (
                    <WizardActionChoice
                        options={ACTION_OPTIONS}
                        value={draftAction}
                        onChange={setDraftAction}
                    />
                )}
            </WizardRightPanel>

            <Modal
                visible={confirmDetach}
                type="surprised"
                title="Detach annex files"
                extraMessage={`You are about to detach ${detachTargets.length} annex file${detachTargets.length === 1 ? "" : "s"} from their base-file.`}
                closable={!applying}
                onClose={() => !applying && setConfirmDetach(false)}
                onClickCancel={() => !applying && setConfirmDetach(false)}
                onClickConfirm={() => applyAction("detach")}
                cancelButtonName="CANCEL"
                cancelButtonDisable={applying}
                confirmButtonName="DETACH"
                confirmButtonDisable={applying}
                confirmButtonLoading={applying}
                modalClass="annex-detach-confirm"
                size="large"
            />
        </div>
    );
};

const BaseFileRow = ({ doc, selected, disabled, onSelect }) => {
    const handleClick = useCallback(() => {
        if (!disabled) onSelect(doc.id);
    }, [disabled, doc.id, onSelect]);

    return (
        <div
            className={`annex-wizard-base-row ${selected ? "selected" : ""} ${disabled ? "disabled" : ""}`}
            onClick={handleClick}
        >
            <span className="annex-wizard-base-no">{doc.documentNo}</span>
            <span className="annex-wizard-base-name truncate-text" title={doc.displayName}>
                {doc.displayName}
            </span>
        </div>
    );
};

export default ManageAnnexesWizard;
